"use client";

import React, { useEffect, useState } from 'react';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExternalLink, Loader2, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { getConsent, startSigning } from '@/features/consent/utils/consent-api';
import { useConsentPolling } from '@/features/consent/hooks/use-consent-polling';
import type { TSigningErrorCode } from '@/features/consent/types';

// Коды, которые обрабатываются отдельно, сюда не входят: у них своя реакция,
// а не тост с готовым текстом.
const ERROR_MESSAGES: Record<
  Exclude<TSigningErrorCode, 'requisites_required' | 'consent_already_signed' | 'provider_rejected'>,
  string
> = {
  provider_unavailable: 'Сервис подписания недоступен, попробуйте позже',
  document_render_failed: 'Не удалось подготовить документ',
};

type Props = {
  isPendingSignature: boolean;
  // Возврат к окну реквизитов. Нужен не только когда их нет вовсе: отправка
  // срывается и на самих данных — например, если Подпислон не принял ФИО.
  onEditRequisites: () => void;
  onAlreadySigned: () => void;
}

// Окно 3 «Передача медиа материалов».
export default function ConsentSignStep({ isPendingSignature, onEditRequisites, onAlreadySigned }: Props)
{
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // Сорваться отправка могла и в прошлый раз — тогда причина уже лежит
  // в состоянии согласия, и показать её надо сразу, не дожидаясь нового отказа.
  useEffect(() => {
    let cancelled = false;

    getConsent()
      .then((state) => {
        if (!cancelled) setLastError(state.last_signing_error ?? null);
      })
      .catch(() => {
        // Не показать прошлую причину — не повод ломать окно.
      });

    return () => { cancelled = true; };
  }, []);

  // Документ уже у провайдера — фотограф мог подписать его в соседней вкладке.
  const { phase } = useConsentPolling({ enabled: isPendingSignature });

  useEffect(() => {
    if (phase === 'signed') {
      onAlreadySigned();
    }
  }, [phase, onAlreadySigned]);

  const handleSign = async () => {
    // Повторное нажатие вернёт прежнюю ссылку, но у сервиса подписания баланс
    // документов — лишний запрос ни к чему.
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const { signing_url } = await startSigning();

      window.location.assign(signing_url);
      // Состояние не сбрасываем: страница уходит на Подпислон.
    } catch (error: any) {
      const code: TSigningErrorCode | undefined = error?.response?.data?.code;

      setIsSubmitting(false);

      if (code === 'requisites_required') {
        toast.error('Сначала заполните реквизиты');
        onEditRequisites();
        return;
      }

      if (code === 'consent_already_signed') {
        onAlreadySigned();
        return;
      }

      // Запрос не приняли — причина приходит прямо в теле, доспрашивать нечего.
      if (code === 'provider_rejected') {
        setLastError(error?.response?.data?.reason || 'Сервис подписания не принял документ');
        return;
      }

      toast.error(
        (code && ERROR_MESSAGES[code as keyof typeof ERROR_MESSAGES])
        || 'Не удалось начать подписание, попробуйте позже'
      );
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Передача медиа материалов</DialogTitle>
        <DialogDescription>
          Документ сформирован по вашим реквизитам. Подписание проходит на стороне
          сервиса «Подпислон».
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 text-sm text-muted-foreground">
        <p>
          По кнопке ниже откроется страница подписания. После подписания вы вернётесь
          в фотобанк, и загрузка станет доступна.
        </p>

        {lastError && (
          <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-foreground">
            <p className="font-medium">Документ не приняли на подписание</p>
            <p className="text-muted-foreground">{lastError}</p>
            <p className="text-muted-foreground">
              Скорее всего дело в реквизитах — проверьте их и отправьте документ заново.
            </p>
          </div>
        )}

        {/*
          Выход к форме есть всегда, пока согласие не подписано: окно
          показывается только при requisites_filled и pending_signature, а в них
          реквизиты пересылать можно. Без него фотограф с опечаткой в ФИО
          упирается в неподписываемое согласие и не может ничего исправить.

          В теле, а не в подвале: две кнопки с длинными подписями там не
          помещаются — у Button в базе whitespace-nowrap, а DialogFooter не
          переносит, — и модалка получала горизонтальную прокрутку. Здесь ссылка
          к тому же стоит ровно под фразой «проверьте реквизиты».
        */}
        <Button
          type="button"
          variant="link"
          onClick={onEditRequisites}
          disabled={isSubmitting}
          className="h-auto p-0 text-foreground"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Изменить реквизиты
        </Button>

        {isPendingSignature && (
          <p className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-foreground">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            Документ уже отправлен на подписание. Если вы подписали его в другой
            вкладке, статус обновится здесь автоматически.
          </p>
        )}
      </div>

      <DialogFooter>
        <Button onClick={handleSign} disabled={isSubmitting} className="w-full sm:w-auto">
          {isSubmitting ? (
            <>
              Готовим документ
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            </>
          ) : (
            <>
              Подписать согласие через Подпислон
              <ExternalLink className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
